import { Box, Typography } from '@mui/material';
import { FONT_ENUM } from '../../../types/global/fonts.types';

const SURFACE_LOW = '#131313';
const SURFACE_HI = '#262626';
const GOLD = '#ffc965';
const MINT = '#00fdc1';
const ON_VARIANT = '#adaaaa';

export interface CreatorStatusStripProps {
    activeJobs: number;
    pendingReview: number;
    completed: number;
    invitations: number;
}

function pad(n: number): string {
    return String(n).padStart(2, '0');
}

export function CreatorStatusStrip({ activeJobs, pendingReview, completed, invitations }: CreatorStatusStripProps) {
    const cards = [
        {
            label: 'Active Jobs',
            value: pad(activeJobs),
            valueColor: '#fff',
            chip: activeJobs > 0 ? 'Live' : null,
            chipColor: MINT,
            chipBg: 'rgba(0,253,193,0.1)',
            pulse: activeJobs > 0,
        },
        {
            label: 'Pending Review',
            value: pad(pendingReview),
            valueColor: GOLD,
            chip: pendingReview > 0 ? 'Sign-Off' : null,
            chipColor: GOLD,
            chipBg: 'rgba(255,201,101,0.1)',
            pulse: false,
        },
        {
            label: 'Completed',
            value: pad(completed),
            valueColor: '#fff',
            chip: completed > 0 ? 'Done' : null,
            chipColor: '#fff',
            chipBg: SURFACE_HI,
            pulse: false,
        },
        {
            label: 'Invitations',
            value: pad(invitations),
            valueColor: MINT,
            chip: invitations > 0 ? 'Action Req.' : null,
            chipColor: '#dfffef',
            chipBg: '#006c50',
            pulse: false,
        },
    ];

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
                gap: 1.5,
            }}>
            {cards.map(card => (
                <Box
                    key={card.label}
                    sx={{
                        p: 2,
                        borderRadius: 1.5,
                        bgcolor: SURFACE_LOW,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                        minWidth: 0,
                    }}>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: 11,
                                letterSpacing: '0.14em',
                                textTransform: 'uppercase',
                                color: ON_VARIANT,
                            }}>
                            {card.label}
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontWeight: 800,
                                fontSize: 24,
                                color: card.valueColor,
                                mt: 0.25,
                                lineHeight: 1.1,
                            }}>
                            {card.value}
                        </Typography>
                    </Box>
                    {card.chip ? (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                px: 1.25,
                                py: 0.5,
                                borderRadius: 999,
                                bgcolor: card.chipBg,
                                color: card.chipColor,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 12,
                                flexShrink: 0,
                            }}>
                            {card.pulse ? (
                                <Box
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        bgcolor: MINT,
                                        boxShadow: '0 0 8px #00fdc1',
                                    }}
                                />
                            ) : null}
                            {card.chip}
                        </Box>
                    ) : null}
                </Box>
            ))}
        </Box>
    );
}
